import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

import {
  BaseFiltersPanel,
  BaseFilterSection,
  filterSelectAllRowSx,
} from '../../Paylinks/components/BaseFiltersPanel';
import { Auth0RoleSummary, MerchantAccess } from '../../../store/modules/merchant/types';
import { MerchantAccessMultiSelect } from './MerchantAccessMultiSelect';
import { getPortalUserRoleLabel } from '../portalUserRoleCatalog';
import { PORTAL_USER_MERCHANT_FILTER_TOOLTIP } from '../portalUserMerchantAccessCopy';

export type PortalUserFiltersFormValues = {
  roleNames: string[];
  merchants: MerchantAccess[];
};

export type PortalUserFilters = {
  roleNames?: string[];
  merchantIds?: string[];
};

const emptyValues: PortalUserFiltersFormValues = {
  roleNames: [],
  merchants: [],
};

const convertToQueryFilters = (data: PortalUserFiltersFormValues): PortalUserFilters => {
  const filters: PortalUserFilters = {};
  if (data.roleNames.length > 0) {
    filters.roleNames = data.roleNames;
  }
  if (data.merchants.length > 0) {
    filters.merchantIds = data.merchants.map((merchant) => merchant.merchantId);
  }
  return filters;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onFiltersApply: (filters: PortalUserFilters) => void;
  roles: Auth0RoleSummary[];
  initialFilterState?: Partial<PortalUserFiltersFormValues>;
}

export const PortalUserFiltersPanel = ({
  isOpen,
  onClose,
  onFiltersApply,
  roles,
  initialFilterState = {},
}: Props) => {
  const [lastApplied, setLastApplied] = useState({
    ...emptyValues,
    ...initialFilterState,
  });

  const { control, watch, reset, handleSubmit, setValue } = useForm<PortalUserFiltersFormValues>({
    defaultValues: {
      ...emptyValues,
      ...initialFilterState,
    },
  });

  const selectedRoleNames = watch('roleNames');

  const onSubmit = (data: PortalUserFiltersFormValues) => {
    onFiltersApply(convertToQueryFilters(data));
    setLastApplied(data);
    onClose();
  };

  const onCancel = () => {
    reset(lastApplied);
    onClose();
  };

  return (
    <BaseFiltersPanel
      isOpen={isOpen}
      onCancel={onCancel}
      onReset={() => reset(emptyValues)}
      onSubmit={handleSubmit(onSubmit)}
      filtersCount={Object.keys(convertToQueryFilters(watch())).length}
    >
      <BaseFilterSection
        title="Roles"
        tooltip="Show users assigned any of the selected roles"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '& > *': {
            height: '24px',
            width: 'fit-content',
          },
          '& span': { fontSize: 14 },
        }}
      >
        <FormControlLabel
          sx={filterSelectAllRowSx}
          control={
            <Checkbox
              indeterminate={
                selectedRoleNames.length > 0 && selectedRoleNames.length < roles.length
              }
              checked={roles.length > 0 && selectedRoleNames.length === roles.length}
              onChange={(event) =>
                setValue('roleNames', event.target.checked ? roles.map((role) => role.name) : [])
              }
            />
          }
          label="Select all"
        />
        {roles.map((role) => (
          <FormControlLabel
            key={role.id}
            control={
              <Checkbox
                checked={selectedRoleNames.includes(role.name)}
                onChange={(event) => {
                  if (event.target.checked) {
                    setValue('roleNames', [...selectedRoleNames, role.name]);
                  } else {
                    setValue(
                      'roleNames',
                      selectedRoleNames.filter((name) => name !== role.name),
                    );
                  }
                }}
              />
            }
            label={getPortalUserRoleLabel(role.name)}
          />
        ))}
      </BaseFilterSection>
      <BaseFilterSection title="Merchant access" tooltip={PORTAL_USER_MERCHANT_FILTER_TOOLTIP}>
        <Controller
          name="merchants"
          control={control}
          render={({ field }) => (
            <Box sx={{ mt: 1 }}>
              <MerchantAccessMultiSelect
                value={field.value}
                onChange={field.onChange}
                label="Merchants"
              />
            </Box>
          )}
        />
      </BaseFilterSection>
    </BaseFiltersPanel>
  );
};
